import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { cloneDeep } from 'lodash';
import { CommonService } from 'src/app/services/common/common.service';
import { DataService } from 'src/app/services/data/data.service';
import {
  AppConfig,
  GenderList,
  MonthList,
  PointCollectDialogEvent,
  PointCollectDialogResult,
  PointCollectStage,
  RedeemType,
  SourceSystem,
} from 'src/app/shared/constants/global-config.constant';
import { PointStructure } from 'src/app/shared/constants/point.constant';
import { PHOTOBOOTH_MODAL_OPTIONS } from 'src/app/shared/constants/modal-options.constant';
import {
  IRequestPointEarn,
  IRequestPointRedeem,
  IResponsePointBalance,
  PointBalanceData,
} from 'src/app/shared/interfaces/interface';
import { environment } from 'src/environments/environment';


@Component({
  selector: 'app-point-collect-dialog',
  templateUrl: './point-collect-dialog.component.html',
  styleUrls: ['./point-collect-dialog.component.scss']
})
export class PointCollectDialogComponent implements OnInit {

  constructor(
    private ngbModal: NgbModal,
    private dataService: DataService,
    public commonService: CommonService
  ) { }

  @Output() onSuccessVerify: EventEmitter<PointCollectDialogResult> = new EventEmitter<PointCollectDialogResult>();
  @Output() onFailedVerify: EventEmitter<PointCollectDialogResult> = new EventEmitter<PointCollectDialogResult>();
  @Output() onCloseDialog: EventEmitter<PointCollectDialogResult> = new EventEmitter<PointCollectDialogResult>();

  currentStage: PointCollectStage = PointCollectStage.PROVIDE_MOBILE_NO;
  PointCollectStage = PointCollectStage;
  claimingPoints = false;
  enteredPhoneNumberRaw: string = '';

  // Date picker data
  days: number[] = [];
  months: { id: number; name: string }[] = [];
  years: number[] = [];
  genders: { id: string; name: string }[] = []
  pointCollected = cloneDeep(PointStructure);
  pointBalanceData: PointBalanceData = null;

  selectedDay: number = 1; // default 1
  selectedMonth: number = 1; // default January (1)
  selectedYear: number = AppConfig.POINT_COLLECT_DOB_YEAR_DEFAULT; // default 2000
  selectedGender: string = AppConfig.POINT_COLLECT_GENDER_DEFAULT; // default
  consentGiven: boolean = false;

  @ViewChild('termModal') termModal: any;
  modalRef: NgbModalRef;

  promoCodeFromRedemption: string = 'XXXXXXXX';
  promoExpiryDateFromRedemption: string = 'XX/XX/XXXX';

  ngOnInit(): void {
    this.initMonths();
    this.initYears();
    this.generateDays(this.selectedMonth, this.selectedYear);
    this.initGenders();
  }

  private initMonths() {
    this.months = MonthList;
  }

  private initGenders() {
    this.genders = GenderList;
  }

  private initYears() {
    const start = AppConfig.POINT_COLLECT_DOB_YEAR_START;
    const end = AppConfig.POINT_COLLECT_DOB_YEAR_END;
    this.years = [];
    for (let y = start; y <= end; y++) {
      this.years.push(y);
    }
  }



  private generateDays(month: number, year: number) {
    const daysInMonth = new Date(year, month, 0).getDate();
    this.days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      this.days.push(d);
    }
    // clamp selectedDay if it exceeds the maximum for the month
    if (!this.selectedDay || this.selectedDay > daysInMonth) {
      this.selectedDay = Math.min(1, daysInMonth) || 1;
    }
  }

  onMonthOrYearChange() {
    this.generateDays(this.selectedMonth, this.selectedYear);
    // If previously selected day exceeds new max, clamp it
    const max = new Date(this.selectedYear, this.selectedMonth, 0).getDate();
    if (this.selectedDay > max) {
      this.selectedDay = max;
    }
  }

  closeDialog() {
    this.onCloseDialog.emit({
      event: PointCollectDialogEvent.CLOSE_DIALOG,
    });
  }

  checkCharacter(event: string) {
    if (event === 'DELETE') {
      this.delCharacter();
    }
    else {
      this.addCharacter(event);
    }
  }

  delCharacter() {
    this.enteredPhoneNumberRaw = this.enteredPhoneNumberRaw.slice(0, -1);
  }

  resetCharacter() {
    this.enteredPhoneNumberRaw = '';
  }

  addCharacter(char: string) {
    // only accept digits and enforce max length 10
    const digit = (char || '').replace(/\D+/g, '');
    if (!digit) return;
    if (this.enteredPhoneNumberRaw.length >= 10) return;
    this.enteredPhoneNumberRaw = this.enteredPhoneNumberRaw + digit;
  }

  // Returns the masked display value for the textbox.
  get maskedPhone(): string {
    return this.formatMaskedPhone(this.enteredPhoneNumberRaw);
  }

  // Keep only digits and max 10
  private normalizeDigits(value: string): string {
    return (value || '').replace(/\D+/g, '').slice(0, 10);
  }

  private formatMaskedPhone(digitsRaw: string): string {
    const digits = this.normalizeDigits(digitsRaw);
    const len = digits.length;
    if (len === 0) return '';

    // If 4 or fewer digits, show them as-is (formatted with hyphens where appropriate)
    const toShow = len <= 4 ? digits : 'X'.repeat(len - 4) + digits.slice(-4);

    // Insert hyphens after 3rd and 6th positions relative to the digit string
    let res = '';
    for (let i = 0; i < toShow.length; i++) {
      res += toShow.charAt(i);
      if ((i === 2 || i === 5) && i !== toShow.length - 1) {
        res += '-';
      }
    }
    return res;
  }

  isMobileNoComplete(): boolean {
    const digits = this.normalizeDigits(this.enteredPhoneNumberRaw);
    if (digits.length === 10 && this.validPhoneNumberPrefix()) {
      return true;
    }
  }

  validPhoneNumberPrefix(): boolean {
    const digits = this.normalizeDigits(this.enteredPhoneNumberRaw);
    const prefix = digits.slice(0, 2);
    const validPrefixes = ['08', '09', '06'];
    return validPrefixes.includes(prefix);
  }

  async submitMobileNo() {
    const isFound = await this.getPointBalance();
    if (!isFound) {
      this.currentStage = PointCollectStage.PROVIDE_INFO;
    }
    else {
      const isEarnSuccess = await this.doEarnPoint(false);
      if (isEarnSuccess) {
        this.currentStage = PointCollectStage.SHOW_POINTS;
      }
    }
  }

  async getPointBalance(): Promise<boolean> {
    let isFound = false;
    try {

      this.commonService.isLoading = true;
      const response: IResponsePointBalance = await this.dataService.getPointBalance(this.enteredPhoneNumberRaw).toPromise();
      if (response) {
        this.pointBalanceData = response.data;
        console.log('Fetched point balance:', this.pointBalanceData);
        isFound = true;

        this.pointCollected = cloneDeep(PointStructure);
        this.convertPointBalanceForDisplay();

      } else {
        this.commonService.showErrorPopup('Error', 'Failed to fetch point balance. Please try again later.');
        console.error('Failed to fetch point balance:', response?.message || 'Unknown error');
      }

    } catch (error) {
      if (error.status !== 404) { // If error is not "not found", do not show error popup
        console.error('Error fetching point balance:', error);
        this.commonService.showErrorPopup('Error', 'An error occurred while fetching point balance. Please try again later.');
      }
    }
    finally {
      this.commonService.isLoading = false;
    }
    return isFound
  }

  convertPointBalanceForDisplay() {
    let i = 0;
    for (let point of this.pointBalanceData.point_list) {
      this.pointCollected[i].id = point.id;
      this.pointCollected[i].claimed = true;
      this.pointCollected[i].earned_at = point.earned_at;
      this.pointCollected[i].expires_at = point.expires_at;
      i++;

      if(i === 10) break; // only display up to 10 points

    }
  }

  async doEarnPoint(firstTime: boolean): Promise<boolean> {
    try {
      this.commonService.isLoading = true;

      let paymentTrx = '';
      if (!environment.production) {
        paymentTrx = '75f2afdc-d671-4de5-8995-ee39c21372d1';
      }
      else {
        paymentTrx = this.commonService.getPaymentTrxId();
      }

      let payloadEarn: IRequestPointEarn = null;

      if (firstTime) {
        payloadEarn = {
          phone_no: this.enteredPhoneNumberRaw,
          birth_date: `${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}-${String(this.selectedDay).padStart(2, '0')} 00:00:00`,
          gender: this.selectedGender,
          source: SourceSystem.SCREENLOCK_V2,
          payment_trx: paymentTrx
        }
      }
      else {
        payloadEarn = {
          phone_no: this.enteredPhoneNumberRaw,
          source: SourceSystem.SCREENLOCK_V2,
          payment_trx: paymentTrx
        }
      }

      const response = await this.dataService.earnPoint(payloadEarn).toPromise();

      return true;
    }
    catch (err) {
      this.commonService.showErrorPopup('Error', `An error occurred while claiming points. \n(${err.error?.message || 'Unknown error'})`);
      console.error('Error claiming points:', err);
      return false
    }
    finally {
      this.commonService.isLoading = false;
      await this.getPointBalance(); // Refresh point balance after earning points
      await this.checkPointEnoughForRedemption();
    }

  }

  async checkPointEnoughForRedemption() {
    if (this.pointBalanceData.point_list.length >= 10) {
      try {
        this.commonService.isLoading = true;
        // * enough for redemption, start redemption flow
        const payloadRedemption: IRequestPointRedeem = {
          phone_no: this.enteredPhoneNumberRaw,
          points_used: 10,
          redeem_type: RedeemType.FREE_TICKET,
          branch_id: Number(this.commonService.getBranchId()),
        }
        const responseRedeem = await this.dataService.redeemPoint(payloadRedemption).toPromise();
        this.promoCodeFromRedemption = responseRedeem.data.promotion_detail.code;
        this.promoExpiryDateFromRedemption = this.convertTimeStampToDisplayFormat(responseRedeem.data.promotion_detail.date_expire);
        this.claimingPoints = true;
      }
      catch (err) {
        this.commonService.showErrorPopup('Error', `An error occurred while redeeming points. \n(${err.error?.message || 'Unknown error'})`);
        console.error('Error redeeming points:', err);
      }
      finally {
        this.commonService.isLoading = false;
      }
    }
  }

  convertTimeStampToDisplayFormat(timestamp: string): string {
    // convert from "2027-02-19T04:48:57.235Z" to "19/02/2027"
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  backToMobile() {
    this.currentStage = PointCollectStage.PROVIDE_MOBILE_NO;
  }

  async submitInfo() {
    const isEarnSuccess = await this.doEarnPoint(true);
    if (isEarnSuccess) {
      this.currentStage = PointCollectStage.SHOW_POINTS;
    }
  }

  submitFinal() {
    this.closeDialog();
  }

  skipConsent() {
    this.consentGiven = false;
    this.closeDialog();
  }

  openTermModal() {
    let options: NgbModalOptions = {
      ...PHOTOBOOTH_MODAL_OPTIONS,
      // windowClass: 'modal-90p',
      size: 'lg'
    }
    this.modalRef = this.ngbModal.open(this.termModal, options);

  }

  onDecline() {
    this.consentGiven = false;
    this.modalRef.close();
  }

  onAccept() {
    this.consentGiven = true;
    this.modalRef.close();
  }

}
